import React from 'react';
import FormTextField from '../../../../components/FormTextField';
import { Button, Dialog, DialogActions, MenuItem, Stack, Typography } from '@mui/material';
import { buttonStyle2, dialogButtonCancel } from '../../../../utils/commonStyle';
import { Formik, Form } from 'formik';
import ModalHeader from '../../../../components/ModalHeader';
import { useRef } from 'react';
import { useTheme } from '@emotion/react';
import { useDispatch, useSelector } from 'react-redux';
import * as yup from 'yup';
import { showmessage } from '../../../../utils/toastr';
import { setLoading } from '../../../../redux/features/appSlice';
import appapi from '../../../../api/apiClient';
import { useState } from 'react';
import { useEffect } from 'react';
import PhoneTextField from '../../../../components/PhoneTextField';
import { matchIsValidTel } from 'mui-tel-input';
import FormSelectField from '../../../../components/FormSelectField';
import { setShipCharterers, setShipMangers, setShipOperators, setShipOwners } from '../../../../redux/features/layoutSlice';
import { refreshUserData } from '../../../../redux/features/authSlice';
import { useTranslation } from 'react-i18next';

const validationSchema = yup.object({
  companyName: yup.string('Company Name').min(3, 'Company Name should be atleast 3 characters').max(100, 'Maximum Character Limit 100').required(' '),
  regAddress: yup.string('Address').min(1, 'Address should be atleast 1 character').max(300, 'Maximum Character Limit 300').required(' '),
  companyType: yup.string('Company Type').required(' '),
  countryName: yup.string('Country Name').required(' '),
  stateName: yup.string('State Name').required(' '),
  zipCode: yup.string('Zipcode').required(' ').min(1, 'Zip Code should be atleast 1 characters').max(8, 'Maximum Character Limit 8').required(' '),
  name: yup.string('Contact person full Name').min(3, 'Contact person full Name should be atleast 3 characters').max(100, 'Maximum Character Limit 100').required(' '),
  email: yup
    .string('Email')
    .matches(/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/, 'Email is Invalid')
    .trim()
    .nullable()
    .required(' '),
  phone: yup
    .string('Phone Number')
    .required()
    .test('is-valid-number', 'Mobile Number is Invalid', (value) => {
      if (value) {
        return matchIsValidTel(value);
      }

      return false;
    }),
});

const companyType = [
  {
    companyId: 'ship manager',
    companyName: 'Ship Manager',
  },
  {
    companyId: 'ship owner',
    companyName: 'Ship Owner',
  },
  {
    companyId: 'charterer',
    companyName: 'Charterer',
  },
  {
    companyId: 'operator',
    companyName: 'Operator',
  },
];

const sx = {
  '& .MuiDialog-container': {
    alignItems: 'center',
    WebkitAlignItems: 'center',
    WebkitBoxAlign: 'center',
  },
  '& .MuiPaper-root': {
    width: '560px',
    height: '100%',
    borderRadius: '4px',
    maxWidth: '736px',
    maxHeight: 'calc(100%)',
    // overflow: 'hidden'
  },
};

export default function AddEditContacts({ modelOpen, onModelHandleClose, contact, type }) {
  const formRef = useRef();
  const theme = useTheme();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { countryList } = useSelector((state) => state.layout);
  const { userId, companyDetails, user } = useSelector((state) => state.auth);
  const [countryId, setCountryId] = useState(null);
  const [contacts, setContacts] = useState([]);

  const [stateList, setStateList] = useState([]);

  const text = theme.palette.primary.text;
  const textLight = theme.palette.primary.textLight;

  useEffect(() => {
    if (contact && contact?.countryName) {
      const idObj = countryList.filter((res) => res.countryName === contact?.countryName)[0];
      if (idObj) {
        setCountryId(idObj._id);
      }
    }
  }, [contact?.countryName]);

  useEffect(() => {
    if (countryId) {
      getStateList();
    }
  }, [countryId]);

  useEffect(() => {
    if (user) {
      setContacts(companyType.filter((res) => res.companyId != user?.companyType));
    }
  }, [user?.companyType]);

  const getStateList = async () => {
    const response = await appapi.get(`common/state/${countryId}`);
    if (response.ok) {
      const data = response.data;
      if (data.status === '7400') {
        setStateList(data.response || []);
      } else {
        setStateList([]);
      }
    }
  };

  const handleAddContact = async (values) => {
    dispatch(setLoading(true));
    const payload = {
      ...values,
      companyId: user?.companyId,
    };

    const response = await appapi.post(`/contact-management/companyContacts`, payload);
    if (response.ok) {
      dispatch(setLoading(false));
      const data = response.data;
      if (data.status === '7400') {
        if (values.companyType === 'ship owner') {
          await initShipOwners();
        }
        if (values.companyType === 'ship manager') {
          await initShipManagers();
        }
        if (values.companyType === 'operator') {
          await initShipOperators();
        }
        if (values.companyType === 'charterer') {
          await initShipCharterers();
        }

        if (!user.contactLength || user.contactLength === 0) {
          dispatch(refreshUserData({ ...user, contactLength: 1 }));
        }

        onModelHandleClose(true);
        showmessage(data.message);
      } else {
        showmessage(data.message, 'error');
      }
    } else {
      dispatch(setLoading(false));
      showmessage(`Something Went Wrong!`, 'error', 2000);
    }
  };

  const handleEditContact = async (values) => {
    const payload = {
      ...values,
      companyId: user?.companyId,
    };
    dispatch(setLoading(true));
    const response = await appapi.put(`contact-management/${contact?._id}`, payload);
    if (response.ok) {
      const data = response.data;
      if (data.status === '7400') {
        if (values.companyType === 'ship owner') {
          await initShipOwners();
        }
        if (values.companyType === 'ship manager') {
          await initShipManagers();
        }
        if (values.companyType === 'operator') {
          await initShipOperators();
        }
        if (values.companyType === 'charterer') {
          await initShipCharterers();
        }
        onModelHandleClose(true);
        showmessage(data.message);
      } else {
        showmessage(data.message, 'error');
      }
      dispatch(setLoading(false));
    } else {
      dispatch(setLoading(false));
      showmessage(`Something Went Wrong!`, 'error', 2000);
    }
  };

  const initShipOwners = async () => {
    const response = await appapi.get(`fleet/getUserForFleet/${user?.companyId}`, {
      type: 'ship owner',
    });
    if (response.ok) {
      const data = response.data;
      if (data.status === '7400') {
        dispatch(setShipOwners(data.response || []));
      }
    }
  };

  const initShipManagers = async () => {
    const response = await appapi.get(`fleet/getUserForFleet/${user?.companyId}`, {
      type: 'ship manager',
    });
    if (response.ok) {
      const data = response.data;
      if (data.status === '7400') {
        dispatch(setShipMangers(data.response || []));
      }
    }
  };

  const initShipOperators = async () => {
    const response = await appapi.get(`fleet/getUserForFleet/${user?.companyId}`, {
      type: 'operator',
    });
    if (response.ok) {
      const data = response.data;
      if (data.status === '7400') {
        dispatch(setShipOperators(data.response || []));
      }
    }
  };

  const initShipCharterers = async () => {
    const response = await appapi.get(`fleet/getUserForFleet/${user?.companyId}`, {
      type: 'charterer',
    });
    if (response.ok) {
      const data = response.data;
      if (data.status === '7400') {
        dispatch(setShipCharterers(data.response || []));
      }
    }
  };

  return (
    <Dialog
      sx={sx}
      open={modelOpen}
      PaperProps={{ sx: { position: 'fixed', top: 0, right: 0, m: 0 } }}
    >
      <Stack
        // className="!h-[100%]"
        style={{ backgroundColor: '#FFFFFF' }}
      >
        <ModalHeader
          label={type === 'add' ? t('Add contact') : t('Edit contact')}
          onClose={() => onModelHandleClose(false)}
        />
        <Stack
          style={{ backgroundColor: '#8993AC26', width: '100%', height: '2px' }}
          className=""
        />
        <Formik
          innerRef={formRef}
          enableReinitialize={false}
          initialValues={{
            companyId: contact?.companyId || '',
            companyName: contact?.companyName || '',
            regAddress: contact?.regAddress || '',
            countryName: contact?.countryName || '',
            zipCode: contact?.zipCode || '',
            stateName: contact?.stateName || '',
            companyType: contact?.companyType || '',
            name: contact?.name || '',
            email: contact?.email || '',
            phoneNumber: contact?.phoneNumber || '',
            phone: (contact?.phoneCode || '') + (contact?.phoneNumber || ''),
            phoneCode: contact?.phoneCode,
          }}
          validationSchema={validationSchema}
          onSubmit={(values) => {
            if (validationSchema.validate(values)) {
              if (type === 'add') {
                handleAddContact(values);
              }
              if (type === 'edit') {
                handleEditContact(values);
              }
            }

          }}
        >
          {({ name, values, touched, errors, handleChange, handleBlur, isValid, dirty, setFieldValue }) => (
            <Form autoComplete="off">
              <Stack
                style={{ backgroundColor: '#F2F3F580', borderRadius: '8px', borderWidth: '1px', borderColor: '#8993AC26' }}
                className="m-[15px] p-[16px]"
              >
                <Typography
                  className="fw-regular ff-Roboto fs-16"
                  color={text}
                >
                  {t('Company information')}
                </Typography>
                <Typography
                  className="fw-regular ff-Roboto fs-12 !mb-[16px]"
                  color={textLight}
                >
                  {t('Some info may be visible to other people using ZeroEmission services')}
                </Typography>
                <Stack>
                  <FormTextField
                    outlineLabel={t('Company name')}
                    name="companyName"
                    requiredMark={true}
                    whiteSpace={true}
                    inputProps={{ maxLength: 101 }}
                  />
                </Stack>
                <Stack>
                  <FormTextField
                    outlineLabel={t('Company type')}
                    fullWidth
                    id={'companyType'}
                    name="companyType"
                    requiredMark={true}
                    select
                    onSelect={(e) => { }}
                  >
                    {contacts?.map((item) => (
                      <MenuItem
                        key={item.companyId}
                        value={item.companyId}
                      >
                        <div style={{ textTransform: 'capitalize' }}>{item.companyName}</div>
                      </MenuItem>
                    ))}
                  </FormTextField>
                </Stack>
                <Stack>
                  <FormTextField
                    outlineLabel={t('Address')}
                    name="regAddress"
                    requiredMark={true}
                    whiteSpace={true}
                    inputProps={{ maxLength: 301 }}
                  />
                </Stack>
                {/* <Stack>
                  <FormTextField
                    placeholder={'Country'}
                    outlineLabel={'Country'}
                    fullWidth
                    id={'countryName'}
                    name="countryName"
                    requiredMark={true}
                    select
                    sx={{ marginBottom: '25px !important' }}
                    onSelect={(e) => {
                      setFieldValue('stateName', undefined);
                      const idObj = countryList.filter((res) => res.countryName === e)[0];
                      if (idObj) {
                        setCountryId(idObj._id);
                      }
                    }}
                  >
                    {countryList?.map((item) => (
                      <MenuItem
                        key={item.countryName}
                        value={item.countryName}
                      >
                        <div style={{ textTransform: 'capitalize' }}>{item.countryName}</div>
                      </MenuItem>
                    ))}
                  </FormTextField>
                </Stack>
                <Stack>
                  <FormTextField
                    placeholder={'State'}
                    outlineLabel={'State'}
                    fullWidth
                    id={'stateName'}
                    name="stateName"
                    requiredMark={true}
                    select
                    sx={{ marginBottom: '25px !important' }}
                    onSelect={(e) => {}}
                  >
                    {stateList?.map((item) => (
                      <MenuItem
                        key={item.stateName}
                        value={item.stateName}
                      >
                        <div style={{ textTransform: 'capitalize' }}>{item.stateName}</div>
                      </MenuItem>
                    ))}
                  </FormTextField>
                </Stack> */}
                <Stack>
                  <FormSelectField
                    name="countryName"
                    label={`${t('Country')} *`}
                    onSelect={(e) => {
                      setFieldValue('stateName', undefined);
                      const idObj = countryList.filter((res) => res.countryName === e)[0];
                      if (idObj) {
                        setCountryId(idObj._id);
                      }
                    }}
                  >
                    {countryList?.map((item) => (
                      <MenuItem
                        key={item.countryName}
                        value={item.countryName}
                      >
                        <div style={{ textTransform: 'capitalize' }}>{item.countryName}</div>
                      </MenuItem>
                    ))}
                  </FormSelectField>
                </Stack>
                <Stack>
                  <FormSelectField
                    name="stateName"
                    label={`${t('State/Province')} *`}
                  >
                    {stateList?.length === 0 && (
                      <MenuItem
                        disabled
                        value=""
                      >
                        <em>{t('No Data Found')}</em>
                      </MenuItem>
                    )}
                    {stateList?.map((item) => (
                      <MenuItem
                        key={item.stateName}
                        value={item.stateName}
                      >
                        <div style={{ textTransform: 'capitalize' }}>{item.stateName}</div>
                      </MenuItem>
                    ))}
                  </FormSelectField>
                </Stack>
                <Stack>
                  <FormTextField
                    outlineLabel={t('Zip Code')}
                    name="zipCode"
                    requiredMark={true}
                    whiteSpace={true}
                    inputProps={{ maxLength: 8 }}
                  />
                </Stack>
              </Stack>
              <Stack
                style={{ backgroundColor: '#F2F3F580', borderRadius: '8px', borderWidth: '1px', borderColor: '#8993AC26' }}
                className="m-[15px] p-[16px]"
              >
                <Typography
                  className="fw-regular ff-Roboto fs-16"
                  color={text}
                >
                  {t('Contact person')}
                </Typography>
                <Typography
                  className="fw-regular ff-Roboto fs-12 !mb-[16px]"
                  color={textLight}
                >
                  {t('Some info may be visible to other people using ZeroEmission services')}
                </Typography>
                <Stack>
                  <FormTextField
                    outlineLabel={t('Full name')}
                    name="name"
                    requiredMark={true}
                    onlyAlpha={true}
                    whiteSpace={true}
                    inputProps={{ maxLength: 101 }}
                  />
                </Stack>
                <Stack>
                  <FormTextField
                    outlineLabel={t('Email')}
                    name="email"
                    requiredMark={true}
                    whiteSpace={true}
                    inputProps={{ maxLength: 51 }}
                    lowerCase={true}
                  />
                </Stack>

                <Stack>
                  <PhoneTextField
                    required
                    variant="outlined"
                    label={t('Phone number')}
                    name="phone"
                    name2="phoneNumber"
                    name3="phoneCode"
                  />
                </Stack>
              </Stack>

              <DialogActions
                className="!mt-[44px] h-[66px] !justify-end p-[0px] sticky bottom-[0]"
                style={{ backgroundColor: '#FFFFFF', zIndex: 1 }}
              >
                <Button
                  sx={[
                    dialogButtonCancel,
                    {
                      color: theme.palette.primary.main,
                      '&:hover': {
                        // backgroundColor: theme.palette.secondary.main,
                        // color: theme.palette.background.main,
                        // borderColor: theme.palette.secondary.main,
                      },
                    },
                  ]}
                  style={{ width: '128px', marginLeft: '80px', height: '40px' }}
                  onClick={() => {
                    onModelHandleClose(false);
                  }}
                >
                  {t('Close')}
                </Button>
                <Button
                  sx={[
                    buttonStyle2,
                    {
                      color: theme.palette.background.main,
                      backgroundColor: theme.palette.primary.dark,
                      '&:hover': {
                        // backgroundColor: theme.palette.secondary.main,
                        // color: theme.palette.background.main,
                      },
                    },
                  ]}
                  style={{ width: '128px', height: '40px' }}
                  disabled={!(dirty && isValid)}
                  type="submit"
                  variant="contained"
                >
                  {type === 'add' ? t('Save') : t('Update')}
                </Button>
                {/* {JSON.stringify(errors)} */}
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Stack>
    </Dialog>
  );
}
